<template>
  <SearchSelect
    v-model="from_"
    :placeholder="placeholder ? placeholder : 'Select phone'"
    :data-test-id="dataTestId"
    data-el="phone-select"
    :data-phone-select-value="from_ ? from_ : '0'"
    :data-phone-select-text="from_ ? getText(from_) : '0'"
    :data-loading="loading ? '1' : '0'"
    :data-mounted="mounted ? '1' : '0'"
    data-wrapper-id="select-phone"
    :data-search-active="searchActive ? '1' : '0'"
    :loading="loading"
    :searchActive="searchActive"
    :options="fromOptions"
    :searchOptions="searchOptions"
    filterPlaceholder="Search phones..."
    @search="onSearch"
    @create="onCreate"
    @change="onChange"
    :allow-create="true"
    icon="phones"
  >
    <template #empty>
      No phones.
      <NuxtLink class="text-link" to="/phone-numbers/create"
        >Create an phone</NuxtLink
      >
    </template>
    <template #footer>
      <NuxtLink class="text-link" to="/phone-numbers/">View all</NuxtLink>
      or

      <NuxtLink class="text-link" to="/phone-numbers/create"
        >create new</NuxtLink
      >
    </template>
  </SearchSelect>
</template>
<script setup lang="ts">
/**
 * Dropdown phone search and select
 */
import {
  GetPhoneNumbersSortEnum,
  type PhoneNumberDto as Phone,
  type PhoneNumberProjection,
} from '@mailslurp/admin-sdk';
import {
  loadFetch,
  SELECT_ALL_PHONES,
} from '../../lib/services/helpers';
import { onMounted, ref, defineAsyncComponent } from 'vue';

import { useStore3 } from '../../store/main';
import { storeToRefs } from 'pinia';
import { type SearchSelectOption as Option } from '../../store/types';
import { navigateTo } from 'nuxt/app';
import { getLogger } from '../../lib/services/getLogger';

const SearchSelect = defineAsyncComponent(() => import('./SearchSelect.vue'));
const store3 = useStore3();
const {
  phoneNumbers: phones /* for non-search */,
  phoneNumbersPagination: phonePagination /* for search */,
  regressionTest,
} = storeToRefs(store3);
const log = getLogger('PhoneSelect');
const props = withDefaults(
  defineProps<{
    defaultPhoneId?: null | string;
    placeholder?: string;
    allowAllPhones?: boolean;
    dataTestId?: string;
  }>(),
  {
    dataTestId: 'phone',
    defaultPhoneId: '',
    placeholder: '',
    allowAllPhones: false,
  },
);
const onCreate = async () => {
  await navigateTo('/phone-numbers/create');
};
const from_ = ref<undefined | null | string>(null);
const loading = ref<boolean>(false);
const searchActive = ref<boolean>(false);
const searchPhones = ref<(Phone | PhoneNumberProjection)[] | null>(null);
const ALL_PHONES_OPTION: Option = {
  text: 'All phones',
  value: SELECT_ALL_PHONES,
};
const mounted = ref(false);
const getText = (value: string) => {
  return (
    fromOptions.value.filter((it) => it.value === value)?.at(0)?.text ?? value
  );
};

const fromOptions = ref<Option[]>([]);
const searchOptions = ref<Option[]>([]);
/**
 * Search phones by query
 * @param query
 * @param mergePagination whether to include non filtered phones so more to choose from
 */
const onSearch = async (query?: string, mergePagination?: boolean) => {
  searchActive.value = (query?.length ?? 0) > 0;
  log(`on search (${searchActive.value}): ${query}`);
  if (searchActive.value) {
    log('Searching... loading = ' + loading.value);
    await loadFetch(
      () =>
        store3.getPhoneNumbersPaginated({
          page: 0,
          size: 100,
          sort: GetPhoneNumbersSortEnum.DESC,
          search: query,
        }),
      store3,
      loading,
    );
    log('Searching complete. loading = ' + loading.value);
    if (mergePagination) {
      log('Merge pagination length ' + phonePagination.value?.content?.length);
      const searchRes = phonePagination.value?.content ?? [];
      const searchResIds = searchRes.map((it) => it.id);
      searchPhones.value = [
        ...searchRes,
        // if merge then include passed phones as well search results but remove duplicates
        ...(mergePagination
          ? phones.value?.content?.filter(
              (it) => !searchResIds.includes(it.id),
            ) ?? []
          : []),
      ];
    } else {
      log(
        'Merge pagination false, length ' +
          phonePagination.value?.content?.length,
      );
      searchPhones.value = phonePagination.value?.content ?? [];
    }
  } else {
    searchPhones.value = null;
  }
  updateOptions();
};

onMounted(async () => {
  if (props.defaultPhoneId || !phones.value?.content?.length) {
    log('Mounted. Fetching phones defaultPhoneId=' + props.defaultPhoneId);
    await loadFetch(
      () =>
        store3.getPhoneNumbers({
          include: props.defaultPhoneId ? [props.defaultPhoneId] : undefined,
        }),
      store3,
      loading,
    );
  } else {
    log('Mounted. Re-use phones');
  }
  updateOptions();
  if (fromOptions.value?.length) {
    if (props.defaultPhoneId) {
      from_.value =
        fromOptions.value.find((it) => it.value === props.defaultPhoneId)
          ?.value ?? fromOptions.value[0].value;
      log(
        'Using defaultPhoneId value from fromOption from_ value',
        from_.value,
      );
    } else {
      from_.value = fromOptions.value[0].value;
      log('Using first fromOption from_ value', from_.value);
    }
  } else {
    log('Using unset from_ value', from_.value);
  }
  mounted.value = true;
  emit('change', from_.value ?? null);
});

const emit = defineEmits<{
  (e: 'change', payload: string | null): void;
}>();

const mapPhoneToOption = (
  phoneArr?: (Phone | PhoneNumberProjection)[] | null,
  isSearch?: boolean,
) => {
  const options: Option[] =
    phoneArr
      ?.map((i: Phone | PhoneNumberProjection) => {
        const text = regressionTest.value ? 'phone-number' : i.phoneNumber;
        return {
          text,
          value: i.id ?? '',
          copyValue: i.phoneNumber ?? '',
          routerLink: '/phones-numbers/' + (i.id ?? ''),
        };
      })
      .reverse() ?? [];
  return [
    props.allowAllPhones && !isSearch ? ALL_PHONES_OPTION : undefined,
    ...options,
  ].filter((it) => !!it);
};

const updateOptions = () => {
  searchOptions.value = mapPhoneToOption(
    searchActive.value ? searchPhones.value : undefined,
    true,
  );
  fromOptions.value = mapPhoneToOption(phones.value?.content, false);
};

const onChange = (newValue: string | null | undefined) => {
  const payload = newValue;
  console.log('PhoneSelect emit change', payload);
  emit('change', payload ?? null);
};
</script>
